/**
 * @file Lead upload files API service.
 * @description List uploaded lead files and download a file by id.
 */
import { apiFetch } from '../api/api';
import { API_ENDPOINTS } from '../constants/api';
import type {
  LeadUploadFile,
  LeadUploadFilesListResult,
  LeadUploadFileDownloadResult,
} from '../types/api/leadUploadFiles';
import { ApiService } from './apiService';
import { buildApiUrl, getAuthToken } from '../utils/api';
import { LeadsExportService } from './leadsExportService';

export type {
  LeadUploadFile,
  LeadUploadFilesListResult,
  LeadUploadFilesPagination,
  LeadUploadFileDownloadResult,
} from '../types/api/leadUploadFiles';

function strField(value: unknown, fallback = ''): string {
  if (value == null || value === '') return fallback;
  return String(value);
}

/** Resolve string / nested relation objects (e.g. `{ name: "Demo" }`) for display. */
function coerceDisplayString(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return text && text !== '[object Object]' ? text : '';
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = coerceDisplayString(item);
      if (text) return text;
    }
    return '';
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return coerceDisplayString(
      obj['name'] ??
        obj['source_name'] ??
        obj['title'] ??
        obj['label'] ??
        obj['value'],
    );
  }
  return '';
}

function pickRelationString(row: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const text = coerceDisplayString(row[key]);
    if (text) return text;
  }
  return '';
}

function rowRecord(row: unknown): Record<string, unknown> {
  return row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
}

function normalizeRow(row: unknown): LeadUploadFile | null {
  const r = rowRecord(row);
  const filename = pickRelationString(r, ['file_name', 'filename', 'original_filename', 'csv_file']);
  const id = strField(r['id']) || filename;
  if (!id) return null;

  return {
    id,
    filename,
    file_url: strField(r['file_url'] ?? r['download_url'] ?? r['url']),
    advertiser_name: pickRelationString(r, ['advertiser_name', 'Advertiser', 'advertiser']),
    campaign_name: pickRelationString(r, ['campaign_name', 'Campaign', 'campaign']),
    status: pickRelationString(r, ['status']),
    source_name: pickRelationString(r, [
      'source_name',
      'Source',
      'source',
      'lead_source',
      'upload_source',
      'selected_source',
    ]),
    total_records: pickRelationString(r, ['total_records', 'total', 'total_count']),
    valid_records: pickRelationString(r, ['valid_records', 'valid', 'inserted_records']),
    duplicate_records: pickRelationString(r, [
      'duplicate_records',
      'duplicates',
      'duplicate_count',
    ]),
    uploaded_at: pickRelationString(r, ['created_at', 'uploaded_at', 'upload_date']),
    updated_at: pickRelationString(r, ['updated_at']),
  };
}

async function enrichSourceNames(
  files: LeadUploadFile[],
  rawRows: unknown[],
): Promise<LeadUploadFile[]> {
  if (!files.some((file) => !file.source_name.trim())) {
    return files;
  }

  let sources: Awaited<ReturnType<typeof ApiService.getSources>> = [];
  try {
    sources = await ApiService.getSources();
  } catch {
    return files;
  }

  const sourceById = new Map(
    sources.map((source) => [String(source.id), source.name]),
  );
  const sourceByName = new Map(
    sources.map((source) => [source.name.trim().toLowerCase(), source.name]),
  );

  return files.map((file, index) => {
    if (file.source_name.trim()) return file;

    const r = rowRecord(rawRows[index]);
    const sourceId = strField(r['source_id'] ?? r['sourceId'] ?? r['SourceId']);
    if (sourceId && sourceById.has(sourceId)) {
      return { ...file, source_name: sourceById.get(sourceId)! };
    }

    const rawSource = coerceDisplayString(r['source'] ?? r['Source']);
    if (rawSource) {
      const matched = sourceByName.get(rawSource.toLowerCase());
      return { ...file, source_name: matched ?? rawSource };
    }

    return file;
  });
}

function extractRows(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];

  const root = raw as Record<string, unknown>;
  const data = root['data'];

  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner['files'])) return inner['files'];
    if (Array.isArray(inner['data'])) return inner['data'];
    if (Array.isArray(inner['uploads'])) return inner['uploads'];
    if (Array.isArray(inner['lead_upload_files'])) return inner['lead_upload_files'];
  }
  if (Array.isArray(root['files'])) return root['files'];
  if (Array.isArray(root['lead_upload_files'])) return root['lead_upload_files'];
  return [];
}

function extractPagination(
  raw: unknown,
  total: number,
  page: number,
  perPage: number,
): LeadUploadFilesListResult['pagination'] {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const pagSource =
    root['pagination'] ??
    (root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
      ? (root['data'] as Record<string, unknown>)['pagination']
      : null);

  if (pagSource && typeof pagSource === 'object') {
    const pag = pagSource as Record<string, unknown>;
    const apiTotal = Number(pag['total'] ?? pag['total_records'] ?? total);
    const apiPerPage = Number(pag['per_page'] ?? pag['perPage'] ?? perPage) || perPage;
    const apiPage = Number(pag['page'] ?? pag['current_page'] ?? page) || page;
    const apiTotalPages = Number(
      pag['total_page'] ?? pag['total_pages'] ?? pag['last_page'] ?? Math.ceil(apiTotal / apiPerPage),
    );

    return {
      page: apiPage,
      per_page: apiPerPage,
      total: apiTotal,
      total_page: Math.max(1, apiTotalPages),
    };
  }

  return {
    page,
    per_page: perPage,
    total,
    total_page: Math.max(1, Math.ceil(total / perPage)),
  };
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
  } catch {
    return match[1].replace(/"/g, '').trim();
  }
}

export class LeadUploadFilesService {
  static async listFiles(page = 1, perPage = 10): Promise<LeadUploadFilesListResult> {
    const endpoint = `${API_ENDPOINTS.LEAD_UPLOAD_FILES}?page=${page}&per_page=${perPage}`;
    const raw = await apiFetch<unknown>(endpoint, 'GET');
    const rawRows = extractRows(raw);
    let files = rawRows
      .map(normalizeRow)
      .filter((row): row is LeadUploadFile => row !== null);

    files = await enrichSourceNames(files, rawRows);

    const pagination = extractPagination(raw, files.length, page, perPage);

    if (pagination.total === files.length && files.length > perPage) {
      return {
        files,
        pagination: {
          page,
          per_page: perPage,
          total: files.length,
          total_page: Math.max(1, Math.ceil(files.length / perPage)),
        },
      };
    }

    return { files, pagination };
  }

  static async downloadFile(
    file: LeadUploadFile,
    fallbackFilename = 'lead-upload.csv',
  ): Promise<LeadUploadFileDownloadResult> {
    const filename = file.filename || fallbackFilename;

    if (file.file_url) {
      const blob = await LeadsExportService.downloadFromExportUrl(file.file_url);
      return { blob, filename };
    }

    if (filename) {
      const blob = await LeadsExportService.downloadFromExportUrl(
        `leads-upload/download/${filename}`,
      );
      return { blob, filename };
    }

    return LeadUploadFilesService.downloadFileById(file.id, filename);
  }

  static async downloadFileById(
    id: string | number,
    fallbackFilename = 'lead-upload.csv',
  ): Promise<LeadUploadFileDownloadResult> {
    const token = getAuthToken();
    const response = await fetch(buildApiUrl(API_ENDPOINTS.leadUploadFileById(id)), {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/json, text/csv, application/octet-stream, */*',
      },
    });

    if (!response.ok) {
      let message = `Download failed: ${response.status}`;
      try {
        const errorBody = await response.json();
        if (
          errorBody &&
          typeof errorBody === 'object' &&
          'message' in errorBody &&
          typeof (errorBody as { message: unknown }).message === 'string'
        ) {
          message = (errorBody as { message: string }).message;
        }
      } catch {
        // ignore JSON parse errors
      }
      throw new Error(message);
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      const payload = await response.json();
      const root =
        payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
      const data =
        root['data'] && typeof root['data'] === 'object'
          ? (root['data'] as Record<string, unknown>)
          : root;
      const downloadUrl = strField(
        data['download_url'] ?? data['file_url'] ?? data['url'],
      );
      const filename = strField(
        data['filename'] ?? data['file_name'] ?? fallbackFilename,
        fallbackFilename,
      );

      if (downloadUrl) {
        const fileResponse = await fetch(
          downloadUrl.startsWith('http')
            ? downloadUrl
            : buildApiUrl(downloadUrl.replace(/^\/api\//, '').replace(/^\//, '')),
          {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!fileResponse.ok) {
          throw new Error(`Download failed: ${fileResponse.status}`);
        }
        return {
          blob: await fileResponse.blob(),
          filename:
            filenameFromContentDisposition(
              fileResponse.headers.get('Content-Disposition'),
            ) ?? filename,
        };
      }

      throw new Error('File download URL not found in response.');
    }

    const headerFilename = filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
    );

    return {
      blob: await response.blob(),
      filename: headerFilename ?? fallbackFilename,
    };
  }

  static triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || 'lead-upload.csv';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  static formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
